import styled from 'styled-components'
import { Box, IconButton, Tooltip, useMediaQuery, useTheme } from '@mui/material'
import MoreHorizIcon from '@mui/icons-material/MoreHoriz'
import SaveIcon from '@mui/icons-material/Save'
import PrintIcon from '@mui/icons-material/Print'
import DescriptionIcon from '@mui/icons-material/Description'
import HistoryIcon from '@mui/icons-material/History'
import PsychologyIcon from '@mui/icons-material/Psychology'
import MedicationIcon from '@mui/icons-material/Medication'
import AssessmentIcon from '@mui/icons-material/Assessment'
import FavoriteIcon from '@mui/icons-material/Favorite'
import ScienceIcon from '@mui/icons-material/Science'
import ChatIcon from '@mui/icons-material/Chat'
import AssignmentIcon from '@mui/icons-material/Assignment'
import EditIcon from '@mui/icons-material/Edit'
import SettingsIcon from '@mui/icons-material/Settings'
import VisibilityIcon from '@mui/icons-material/Visibility'

const TopTabsContainer = styled.div`
  position: relative;
  top: 0;
  left: 0;
  right: 0;
  z-index: 1400 !important; /* Above drawer (1300) and backdrop (1200) to ensure tabs are always clickable */
  background: linear-gradient(135deg, rgba(255, 255, 255, 0.98) 0%, rgba(248, 250, 252, 0.98) 100%);
  backdrop-filter: blur(20px) saturate(180%);
  -webkit-backdrop-filter: blur(20px) saturate(180%);
  border-bottom: 1px solid rgba(102, 126, 234, 0.15);
  box-shadow: 0 4px 16px rgba(102, 126, 234, 0.1), 0 2px 8px rgba(118, 75, 162, 0.08);
  width: 100%;
  max-width: 100vw;
  display: flex;
  flex-direction: column;
  align-items: stretch;
  pointer-events: auto !important; /* Ensure clicks work - override any parent styles */
  transition: all 0.3s ease;
  visibility: visible;
  opacity: 1;

  @media (max-width: 480px) {
    min-width: 100vw;
    width: 100vw;
  }

  @media print {
    display: none;
  }
`

const TopTabsHeaderRow = styled.div`
  position: relative;
  display: flex;
  align-items: center;
  width: 100%;
  min-height: 48px;

  @media (max-width: 768px) {
    flex-direction: column;
    align-items: stretch;
    min-width: 100vw;
  }
`

const TabsScrollContainer = styled.div`
  display: flex;
  align-items: center;
  min-height: 48px;
  height: 48px;
  padding: 0 16px;
  gap: 8px;
  position: relative;
  z-index: 1;
  pointer-events: auto;
  flex: 1;
  overflow-x: auto;
  overflow-y: hidden;
  scrollbar-width: none;
  -ms-overflow-style: none;
  min-width: 0;
  width: 100%;

  &::-webkit-scrollbar {
    display: none;
  }

  @media (max-width: 768px) {
    padding: 0 12px;
    gap: 6px;
    width: 100%;
    min-width: 100%;
  }

  @media (max-width: 480px) {
    padding: 0 8px;
    gap: 4px;
    width: 100vw;
    min-width: 100vw;
  }
`

const ActionButtonsContainer = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 0 16px;
  flex-shrink: 0;
  border-left: 1px solid rgba(102, 126, 234, 0.15);
  min-height: 48px;
  height: 48px;

  @media (max-width: 768px) {
    padding: 0 12px;
    gap: 6px;
    border-left: none;
    border-top: 1px solid rgba(102, 126, 234, 0.15);
    min-height: 44px;
    height: 44px;
    width: 100%;
    justify-content: flex-end;
  }
`

const ActionButton = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  padding: ${(props) => (props.isMobile ? '6px 10px' : '8px 16px')};
  border-radius: 10px;
  font-size: ${(props) => (props.isMobile ? '13px' : '14px')};
  font-weight: 600;
  font-family: inherit;
  cursor: pointer !important;
  transition: all 0.2s cubic-bezier(0.4, 0, 0.2, 1);
  white-space: nowrap;
  pointer-events: auto !important; /* Ensure button is always clickable */
  position: relative;
  z-index: 1401 !important; /* Above container to ensure clicks work */
  border: ${(props) =>
    props.variant === 'outlined'
      ? '1.5px solid rgba(102, 126, 234, 0.4)'
      : 'none'};
  background: ${(props) =>
    props.variant === 'contained'
      ? 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)'
      : 'rgba(255, 255, 255, 0.95)'};
  color: ${(props) => (props.variant === 'contained' ? '#ffffff' : '#667eea')};
  box-shadow: ${(props) =>
    props.variant === 'contained'
      ? '0 2px 8px rgba(102, 126, 234, 0.3)'
      : '0 1px 3px rgba(0, 0, 0, 0.1)'};

  &:hover:not(:disabled) {
    background: ${(props) =>
      props.variant === 'contained'
        ? 'linear-gradient(135deg, #5a67d8 0%, #6b46c1 100%)'
        : 'rgba(102, 126, 234, 0.1)'};
    transform: translateY(-1px);
    box-shadow: ${(props) =>
      props.variant === 'contained'
        ? '0 4px 12px rgba(102, 126, 234, 0.4)'
        : '0 2px 6px rgba(102, 126, 234, 0.2)'};
  }

  &:active:not(:disabled) {
    transform: translateY(0);
  }

  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
    transform: none;
  }

  &:focus {
    outline: 2px solid rgba(102, 126, 234, 0.5);
    outline-offset: 2px;
  }
`

const TabItem = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  background: ${(props) => 
    props.selected 
      ? 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)'
      : 'rgba(255, 255, 255, 0.9)'};
  backdrop-filter: ${(props) => props.selected ? 'blur(10px) saturate(150%)' : 'blur(8px) saturate(120%)'};
  -webkit-backdrop-filter: ${(props) => props.selected ? 'blur(10px) saturate(150%)' : 'blur(8px) saturate(120%)'};
  color: ${(props) => (props.selected ? '#ffffff' : '#64748b')};
  border: ${(props) => 
    props.selected 
      ? '1.5px solid rgba(102, 126, 234, 0.4)'
      : '1px solid rgba(148, 163, 184, 0.2)'};
  padding: ${(props) => (props.isMobile ? '8px 12px' : '10px 18px')};
  border-radius: 12px;
  font-size: ${(props) => (props.isMobile ? '13px' : '14px')};
  font-weight: ${(props) => (props.selected ? '600' : '500')};
  font-family: inherit;
  cursor: pointer;
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  white-space: nowrap;
  flex-shrink: 0;
  position: relative;
  z-index: 2;
  pointer-events: auto !important;
  user-select: none;
  box-shadow: ${(props) => 
    props.selected 
      ? '0 4px 12px rgba(102, 126, 234, 0.3), 0 2px 6px rgba(118, 75, 162, 0.2)'
      : '0 2px 4px rgba(0, 0, 0, 0.05)'};

  & svg {
    flex-shrink: 0;
  }

  & span {
    display: inline-block;
  }

  &:hover {
    background: ${(props) => 
      props.selected 
        ? 'linear-gradient(135deg, #5a67d8 0%, #6b46c1 100%)'
        : 'rgba(102, 126, 234, 0.1)'};
    color: ${(props) => (props.selected ? '#ffffff' : '#475569')};
    border-color: ${(props) => 
      props.selected 
        ? 'rgba(102, 126, 234, 0.5)'
        : 'rgba(102, 126, 234, 0.3)'};
    transform: translateY(-2px);
    box-shadow: ${(props) => 
      props.selected 
        ? '0 6px 16px rgba(102, 126, 234, 0.4), 0 3px 8px rgba(118, 75, 162, 0.3)'
        : '0 4px 8px rgba(102, 126, 234, 0.15)'};
  }

  &:active {
    transform: translateY(0);
    box-shadow: ${(props) => 
      props.selected 
        ? '0 2px 8px rgba(102, 126, 234, 0.3)'
        : '0 1px 3px rgba(0, 0, 0, 0.1)'};
  }

  &:focus {
    outline: 2px solid rgba(102, 126, 234, 0.5);
    outline-offset: 2px;
  }

  @media (max-width: 768px) {
    padding: 8px 12px;
    font-size: 13px;
    border-radius: 10px;
  }
`

const MoreButton = styled.button`
  background: ${(props) => (props.hasSecondarySelected ? '#3d1876' : '#f8f9fa')};
  background-color: ${(props) =>
    props.hasSecondarySelected ? '#3d1876 !important' : '#f8f9fa !important'};
  color: ${(props) => (props.hasSecondarySelected ? '#ffffff' : '#495057')};
  border: ${(props) => (props.hasSecondarySelected ? '1px solid #3d1876' : '1px solid #dee2e6')};
  padding: 8px 12px;
  border-radius: 6px;
  font-size: 14px;
  font-weight: ${(props) => (props.hasSecondarySelected ? '600' : '500')};
  font-family: inherit;
  cursor: pointer;
  transition: all 0.2s ease;
  white-space: nowrap;
  flex-shrink: 0;
  position: relative;
  z-index: 2;
  pointer-events: auto;
  user-select: none;
  display: flex;
  align-items: center;
  gap: 4px;

  &:hover {
    background: ${(props) => (props.hasSecondarySelected ? '#2d1066' : '#e9ecef')};
    background-color: ${(props) =>
      props.hasSecondarySelected ? '#2d1066 !important' : '#e9ecef !important'};
    color: ${(props) => (props.hasSecondarySelected ? '#ffffff' : '#212529')};
    border-color: ${(props) => (props.hasSecondarySelected ? '#2d1066' : '#adb5bd')};
    transform: translateY(-1px);
    box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
  }

  &:active {
    transform: translateY(0);
    box-shadow: 0 1px 2px rgba(0, 0, 0, 0.1);
  }

  &:focus {
    outline: 2px solid #3d1876;
    outline-offset: 2px;
  }

  @media (max-width: 768px) {
    padding: 6px 10px;
    font-size: 13px;
  }
`

const MobileScrollIndicator = styled.div`
  position: absolute;
  right: 0;
  top: 0;
  bottom: 0;
  width: 20px;
  background: linear-gradient(to left, rgba(255, 255, 255, 1), rgba(255, 255, 255, 0));
  pointer-events: none;

  @media (min-width: 769px) {
    display: none;
  }
`

// Icon mapping for menu items
const getTabIcon = (itemId) => {
  const iconMap = {
    therapy_report: PsychologyIcon,
    prescription: MedicationIcon,
    screening_report: AssessmentIcon,
    assessment_report: AssignmentIcon,
    patient_history: HistoryIcon,
    'report-preview': PrintIcon,
    print_rx: PrintIcon,
    rx: VisibilityIcon,
    vitals: FavoriteIcon,
    investigations: ScienceIcon,
    chat: ChatIcon,
    activity_plans: AssignmentIcon,
    consultation_screening_report: DescriptionIcon,
    assessments: AssignmentIcon,
  }
  return iconMap[itemId] || DescriptionIcon
}

// Short, intuitive labels shown next to an icon on mobile — non-technical users read "Preview"/"H"
// far quicker than guessing an icon alone. The prescription editor is the default page so it has no
// tab (handled in render); the read-only prescription view is "Preview", assessments is "Assess".
const TAB_INITIALS = {
  prescription: 'Rx',
  rx: 'Preview',
  therapy_report: 'T',
  screening_report: 'S',
  consultation_screening_report: 'S',
  assessment_report: 'A',
  patient_history: 'H',
  vitals: 'V',
  investigations: 'I',
  'report-preview': 'Pr',
  print_rx: 'Pr',
  activity_plans: 'Plans',
  chat: 'Chat',
  assessments: 'Assess',
}

const getTabInitials = (item) => {
  if (TAB_INITIALS[item?.id]) return TAB_INITIALS[item.id]
  // Fallback: initials from the label's words (max 2 letters), ignoring emoji/punctuation.
  const words = (item?.label || '').replace(/[^A-Za-z0-9 ]/g, '').trim().split(/\s+/).filter(Boolean)
  if (words.length === 0) return '•'
  if (words.length === 1) return words[0].slice(0, 2)
  return (words[0][0] + words[1][0]).toUpperCase()
}

const DraftBadge = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  width: 100%;
  padding: 8px 16px;
  background: linear-gradient(180deg, #FEF3C7 0%, #FEF9E8 100%);
  border-top: 1px solid #FCD34D;
  transition: all 0.2s ease;

  @media (max-width: 768px) {
    padding: 7px 12px;
    gap: 6px;
  }
`

const DraftBadgeText = styled.div`
  display: flex;
  flex-direction: column;
  gap: 1px;
`

const DraftBadgeTitle = styled.span`
  font-size: 12px;
  font-weight: 700;
  color: #92400E;
  letter-spacing: 0.3px;
  text-transform: uppercase;
  line-height: 1.2;

  @media (max-width: 768px) {
    font-size: 11px;
  }
`

const DraftBadgeSubtitle = styled.span`
  font-size: 10px;
  font-weight: 400;
  color: #B45309;
  line-height: 1.2;

  @media (max-width: 768px) {
    font-size: 9px;
  }
`

const SessionTopTabs = ({ 
  menuItemsList, 
  selectedMenuItem, 
  setSelectedMenuItem, 
  onOpenContentDrawer,
  actionButtons, // { save, saveAndPrint, isLoading, showSaveButtons }
  therapyReportStatus, // Status of therapy report for draft badge
  onSettingsClick, // Callback for settings button click
  onOpenDrawer, // Callback to open drawer for secondary sections
}) => {
  const theme = useTheme()
  const isMobile = useMediaQuery(theme.breakpoints.down('md'))
  
  // Filter out video_call as it's handled in the main content area
  const filteredMenuItems = menuItemsList?.filter((item) => item?.id !== 'video_call') || []

  // Primary section is the first item (loaded in main area, not a drawer)
  const primarySection = filteredMenuItems[0]
  // All other items are secondary (open in drawers)
  const secondarySections = filteredMenuItems.slice(1)

  // Handle button click - all items are buttons
  // Primary section button: shows content in main area (no drawer)
  // Secondary section buttons: open drawer with content
  const handleButtonClick = (itemId) => {
    setSelectedMenuItem(itemId)
    
    // Notify parent to open drawer if this is a secondary section
    // Check if it's primary - if so, close drawer. If secondary, open drawer.
    if (onOpenDrawer && typeof onOpenDrawer === 'function') {
      onOpenDrawer(itemId)
    }
  }

  const showActionButtons = actionButtons?.showSaveButtons && (actionButtons?.save || actionButtons?.saveAndPrint)

  return (
    <TopTabsContainer className="session-top-tabs hide-on-print-only">
      <TopTabsHeaderRow>
        <TabsScrollContainer>
          {/* Primary Section Button — the default page (e.g. prescription) is already shown in the
              main area, so on mobile it needs no tab; everything else opens as a drawer. Desktop keeps
              it for parity with the split layout. */}
          {!isMobile && primarySection && (() => {
            const IconComponent = getTabIcon(primarySection.id)
            const isSelected = primarySection.id === selectedMenuItem
            return (
              <ActionButton
                key={primarySection.id}
                onClick={(e) => {
                  e.preventDefault()
                  e.stopPropagation()
                  handleButtonClick(primarySection.id)
                }}
                variant={isSelected ? "contained" : "outlined"}
                isMobile={isMobile}
                title={primarySection.label}
                aria-label={primarySection.label}
                style={{
                  background: isSelected
                    ? 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)'
                    : 'rgba(255, 255, 255, 0.95)',
                  color: isSelected ? '#ffffff' : '#667eea',
                  ...(isMobile ? { minWidth: '44px', padding: '8px 10px' } : {}),
                }}
              >
                {isMobile ? (
                  <span style={{ fontWeight: 700, fontSize: '15px', lineHeight: 1, letterSpacing: '0.2px' }}>
                    {getTabInitials(primarySection)}
                  </span>
                ) : (
                  <>
                    <IconComponent sx={{ fontSize: '18px', mr: 0.5 }} />
                    <span>{primarySection.label}</span>
                  </>
                )}
              </ActionButton>
            )
          })()}
          {/* Secondary Sections as Buttons - Open drawers */}
          {secondarySections.map((item) => {
            const IconComponent = getTabIcon(item.id)
            const isSelected = item.id === selectedMenuItem
            return (
              <ActionButton
                key={item.id}
                onClick={(e) => {
                  e.preventDefault()
                  e.stopPropagation()
                  handleButtonClick(item.id)
                }}
                variant={isSelected ? "contained" : "outlined"}
                isMobile={isMobile}
                title={item?.label}
                aria-label={item?.label}
                style={{
                  // Highlight if this drawer is currently open
                  background: isSelected
                    ? 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)'
                    : 'rgba(255, 255, 255, 0.95)',
                  color: isSelected ? '#ffffff' : '#667eea',
                  ...(isMobile ? { minWidth: '44px', padding: '8px 10px' } : {}),
                }}
              >
                {isMobile ? (
                  <>
                    <IconComponent sx={{ fontSize: '16px', mr: 0.5 }} />
                    <span style={{ fontWeight: 700, fontSize: '14px', lineHeight: 1, letterSpacing: '0.2px' }}>
                      {getTabInitials(item)}
                    </span>
                  </>
                ) : (
                  <>
                    <IconComponent sx={{ fontSize: '18px', mr: 0.5 }} />
                    <span>{item?.label}</span>
                  </>
                )}
              </ActionButton>
            )
          })}
          <MobileScrollIndicator />
        </TabsScrollContainer>
        
        {/* Action Buttons Section */}
        {(showActionButtons || onSettingsClick) && (
          <ActionButtonsContainer isMobile={isMobile}>
            {onSettingsClick && (selectedMenuItem === 'therapy_report' || selectedMenuItem === 'screening_report' || selectedMenuItem === 'assessment_report') && (
              <Tooltip
                title={
                  selectedMenuItem === 'screening_report'
                    ? 'EMR Sections Print Settings'
                    : selectedMenuItem === 'assessment_report'
                      ? 'Assessment Report Settings'
                      : 'Editor Settings'
                }
                arrow
              >
                <ActionButton
                  onClick={onSettingsClick}
                  variant="outlined"
                  isMobile={isMobile}
                >
                  <SettingsIcon sx={{ fontSize: { xs: '16px', md: '18px' }, mr: { xs: 0, md: 0.5 } }} />
                  {!isMobile && <span>Settings</span>}
                </ActionButton>
              </Tooltip>
            )}
            {(actionButtons?.saveAndPrint || actionButtons?.print) && (
              <Tooltip title={actionButtons?.print ? "Print" : "Save and Print"} arrow>
                <span>
                  <ActionButton
                    onClick={actionButtons.print || actionButtons.saveAndPrint}
                    disabled={actionButtons.isLoading}
                    variant="outlined"
                    isMobile={isMobile}
                  >
                    <PrintIcon sx={{ fontSize: { xs: '16px', md: '18px' }, mr: { xs: 0, md: 0.5 } }} />
                    {!isMobile && <span>{actionButtons?.print ? 'Print' : 'Save & Print'}</span>}
                  </ActionButton>
                </span>
              </Tooltip>
            )}
            {actionButtons?.save && (
              <Tooltip title="Save" arrow>
                <span>
                  <ActionButton
                    onClick={actionButtons.save}
                    disabled={actionButtons.isLoading}
                    variant="contained"
                    isMobile={isMobile}
                  >
                    <SaveIcon sx={{ fontSize: { xs: '16px', md: '18px' }, mr: { xs: 0, md: 0.5 } }} />
                    {!isMobile && <span>Save</span>}
                  </ActionButton>
                </span>
              </Tooltip>
            )}
          </ActionButtonsContainer>
        )}
      </TopTabsHeaderRow>

      {/* Draft strip - shown below tabs/actions header to avoid overlap with content */}
      {selectedMenuItem === 'therapy_report' && therapyReportStatus !== 'completed' && (
        <DraftBadge>
          <EditIcon sx={{ fontSize: { xs: '16px', md: '18px' }, color: '#F59E0B' }} />
          <DraftBadgeText>
            <DraftBadgeTitle>Draft Report</DraftBadgeTitle>
            <DraftBadgeSubtitle>Not yet finalized</DraftBadgeSubtitle>
          </DraftBadgeText>
        </DraftBadge>
      )}
    </TopTabsContainer>
  )
}

export default SessionTopTabs
